import { TFunction } from 'react-i18next'
import * as yup from 'yup'
import { AddCustomerPayload, CustomerEntityType } from 'types/customers'
import { validateInputField } from '../utils'

const NAME_MIN_LENGTH = 2
const NAME_MAX_LENGTH = 250

export const addCustomerDetailsSchema = (t: TFunction<'translation', undefined>) => {
  const customerEntityType = yup.string().trim().required()
  const customerType = yup.string().trim().required()
  const externalReference = yup.string().trim()
  const businessName = validateInputField({
    t,
    label: t('customers.businessName'),
    minLength: NAME_MIN_LENGTH,
    maxLength: NAME_MAX_LENGTH,
  })
  const industry = validateInputField({ t, label: t('customers.industry') })
  const industryName = yup.string().label(t('customers.industry')).notRequired()
  const businessDescription = validateInputField({
    t,
    label: t('customers.businessDescription'),
    minLength: 3,
    maxLength: 50,
  })
  const registrationNumber = validateInputField({
    t,
    label: t('customers.businessRegistrationNumber'),
    minLength: 5,
    maxLength: 20,
    allowOnlyAlphanumericAndSpecialCharacters: true,
  })
  const businessMonthlyVolume = validateInputField({
    t,
    label: t('customers.businessMonthlyVolume'),
  })
  const businessMonthlyVolumeName = yup
    .string()
    .label(t('customers.businessMonthlyVolume'))
    .notRequired()
  const businessTaxResidency = validateInputField({ t, label: t('customers.businessTaxResidency') })
  const businessTaxResidencyName = yup
    .string()
    .label(t('customers.businessTaxResidency'))
    .notRequired()

  const firstName = validateInputField({
    t,
    label: t('customers.representative.firstName'),
    minLength: NAME_MIN_LENGTH,
    maxLength: NAME_MAX_LENGTH,
    disallowNumericCharacters: true,
  })
  const lastName = validateInputField({
    t,
    label: t('customers.representative.lastName'),
    minLength: NAME_MIN_LENGTH,
    maxLength: NAME_MAX_LENGTH,
    disallowNumericCharacters: true,
  })

  const personalIdNumber = validateInputField({
    t,
    label: t('customers.personalIdNumber'),
    minLength: 4,
    maxLength: 40,
    disallowNumericCharacters: false,
  })

  const nationality = yup
    .string()
    .label(t('customers.representative.nationality'))
    .test({
      test: (value, ctx) => {
        const isIndividualCustomer =
          ctx?.parent?.customerEntityType === CustomerEntityType.INDIVIDUAL

        if (isIndividualCustomer && !value) {
          ctx.schema.required()

          return ctx.createError({
            message: t?.('fieldIsARequiredField', {
              field: t('customers.representative.nationality'),
              interpolation: { escapeValue: false },
            }),
          })
        }
        return true
      },
    })
  const nationalityName = yup
    .string()
    .label(t('customers.representative.nationality'))
    .notRequired()
  const dateOfBirth = yup.string().trim().label(t('formFields.dateOfBirth')).required()
  const addressLine1 = validateInputField({
    t,
    label: t('addressLine1'),
    minLength: 3,
    maxLength: 200,
  })
  const addressLine2 = validateInputField({
    t,
    label: t('addressLine2'),
    minLength: 3,
    maxLength: 200,
  }).notRequired()
  const countryCode = validateInputField({ t, label: t('country') })
  const countryName = yup.string().label(t('country')).notRequired()
  const stateCode = yup
    .string()
    .label(t('customers.address.state'))
    .test({
      test: (value, ctx) => {
        const isUsCountry = ctx?.parent?.countryCode === 'US'
        const isIndividualCustomer =
          ctx?.parent?.customerEntityType === CustomerEntityType.INDIVIDUAL

        if (isUsCountry && isIndividualCustomer && !value) {
          ctx.schema.required()

          return ctx.createError({
            message: t?.('fieldIsARequiredField', {
              field: t('customers.address.state'),
              interpolation: { escapeValue: false },
            }),
          })
        }
        return true
      },
    })
  const stateName = yup.string().label(t('state')).notRequired()
  const city = validateInputField({ t, label: t('city'), minLength: 2, maxLength: 50 })
  const postalCode = validateInputField({ t, label: t('postCode'), minLength: 2, maxLength: 20 })
  const individualCustomerDescription = validateInputField({
    t,
    label: t('customers.internalCustomerDescription'),
    minLength: 3,
    maxLength: 50,
  })

  const formFields = {
    // General
    customerEntityType,
    customerType,
    externalReference,
    addressLine1,
    addressLine2,
    city,
    postalCode,
    countryCode,
    countryName,
    stateCode,
    stateName,
    nationality,
    // Business customer
    businessName,
    businessDescription,
    registrationNumber,
    businessTaxResidency,
    businessMonthlyVolume,
    industry,
    industryName,
    businessMonthlyVolumeName,
    businessTaxResidencyName,
    // Individual customer
    firstName,
    lastName,
    dateOfBirth,
    personalIdNumber,
    nationalityName,
    individualCustomerDescription,
  }

  return yup.object<Partial<Record<keyof AddCustomerPayload, yup.AnySchema>>>(formFields)
}
